/**
 * CookieBanner — aviso informativo, NO panel de consentimiento.
 *
 * Antes tenía "Aceptar todas" / "Rechazar todas". El botón de rechazar guardaba
 * la cadena "rejected" en localStorage y no desactivaba nada — porque no hay
 * nada que desactivar: TAH solo instala cookies técnicas necesarias (sesión de
 * dueño, sesión de trabajador, CSRF de Google). Un botón que dice que rechaza y
 * no rechaza es peor que no tenerlo.
 *
 * El art. 22.2 LSSI excluye del consentimiento a las cookies técnicas, así que
 * pedir permiso sobraba. Se queda un aviso de una línea con enlace a la política.
 *
 * ⚠️ Si algún día entra analítica (Google Analytics o similar), esto TIENE que
 * volver a ser un consentimiento real, con rechazo previo a la instalación.
 * Ver docs/LEGAL.md.
 */
import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Info } from "lucide-react";

const KEY = "tah_cookie_consent";

// La conservan llamadas antiguas; ya no gobierna nada porque no hay cookies
// opcionales que activar. Se mantiene para no romper a quien la importe.
export function hasConsent() {
  return true;
}

// Barra en flujo (nunca `fixed`), mismo patrón visual que `TrialBanner`/
// `ImpersonationBanner` de `AppLayout.jsx`: franja fina con `border-b`, icono +
// texto a la izquierda, botón a la derecha — empuja el contenido, no lo tapa.
//
// `variant="dark"` es la única variante alternativa: la usa `ModoTrabajador.jsx`
// para la pantalla "Marca", que es 100% oscura de forma fija y no participa del
// toggle de tema claro/oscuro del resto de la app — por eso usa colores fijos
// (clases Tailwind arbitrarias) en vez de los tokens `dark:` reales, que aquí no
// aplicarían. Paleta ad-hoc documentada en `oficina-tah/tareas/
// task-reposicionamiento-cookiebanner-implementacion/tarea.md` ("Riesgo
// específico — ModoTrabajador.jsx"), validada por Arquitectura.
export default function CookieBanner({ variant }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const isDark = variant === "dark";

  useEffect(() => {
    if (!localStorage.getItem(KEY)) setVisible(true);
  }, []);

  const entendido = () => {
    localStorage.setItem(KEY, "seen");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className={
        isDark
          ? "flex flex-wrap items-center gap-3 border-b border-white/[0.14] bg-[#111111] px-4 py-2 text-sm text-[#F3EEE3]"
          : "flex flex-wrap items-center gap-3 border-b border-brand-edge bg-card px-4 py-2 text-sm text-brand-stone"
      }
      data-testid="cookie-banner"
    >
      <Info size={16} className="shrink-0" />
      <p className="min-w-[180px] flex-1">
        <Trans
          i18nKey="cookieBanner.text"
          components={[<a key="0" href="/cookies" className={isDark ? "underline" : "underline hover:text-brand-ink"} />]}
        />
      </p>
      <button
        onClick={entendido}
        className={
          isDark
            ? "shrink-0 rounded-md bg-[#C27D56] px-3 py-1.5 text-xs font-semibold text-[#1A1A1A]"
            : "shrink-0 rounded-md bg-brand-ink px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-ink/90 dark:bg-brand-terracotta dark:text-black dark:hover:bg-brand-terracottaDark"
        }
        data-testid="cookie-banner-ok"
      >
        {t("cookieBanner.gotIt")}
      </button>
    </div>
  );
}
